import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useParams, useRouteMatch} from 'react-router-dom';
import {UserCard} from '../../../../../../backend/interfaces/user-card';
import {UserCardForm} from '../../../../../../components/user-card-form/user-card-form';
import {useOffice} from '../../../../../../contexts/office/context';
import {useSnackBar} from '../../../../../../contexts/snack-bar/context';
import {translations} from '../../../../../../i18n/translations';
import {UserCardsRepository} from '../../../../../../repositories/user-cards';
import {CaregiverMode, ScreenPaths} from '../../../../../../enums';

export interface UserCardProfessionalCaregiverOfficeScreenProps {
  canWrite: boolean;
  titleCrumbs?: Array<{label: string; link: string}>;
}

export default function UserCardProfessionalCaregiverOfficeScreen({
  canWrite,
  titleCrumbs,
}: UserCardProfessionalCaregiverOfficeScreenProps) {
  let {id, entityId}: {id?: string; entityId: string} = useParams();
  const snackBarContext = useSnackBar();
  const officeContext = useOffice();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();

  let history = useHistory();
  const {t} = useTranslation();

  // Validate and get the card
  useEffect(() => {
    const getCard = async () => {
      if (id) {
        try {
          // Get card
          const cardDetails: UserCard = await UserCardsRepository.instance.getById(id);

          if (cardDetails) {
            // Set card for edit
            officeContext.setUserCardForm(cardDetails);
          } else {
            // Error
            throw new Error('Empty card.');
          }
        } catch (error) {
          // Log
          console.debug('🔴 Could not get card.', error);

          // Show toast error
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.user_cards.edit.errors.card_does_not_exist_error),
          });

          // Redirect
          history.push(`${url.replace(`/${id}`, '')}`);
        }
      } else {
        // Log
        console.debug('🔴 Empty card id.');

        // Show toast error
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.user_cards.edit.errors.card_does_not_exist_error),
        });

        // Redirect
        history.push(url);
      }
    };

    // Get card
    getCard();

    // Clean up
    return () => {
      // Clear card
      officeContext.clearUserCardForm();

      // Log
      console.debug('🧹 Card form cleaned in office context.');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Only professional caregivers that are managers or administrators can
  // update cards. All other users can only view the details of the card.
  return (
    <UserCardForm
      caregiverMode={CaregiverMode.Professional}
      canWrite={canWrite}
      redirectUrl={`${url.replace(`/${id}`, '')}`}
      showPageTitle={false}
      showExitButton={false}
      titleCrumbs={
        titleCrumbs ?? [
          {
            label: t(translations.office.professional_caregiver.tabs.digital_inclusion),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}`,
          },
          {
            label: t(translations.office.professional_caregiver.tabs.user_cards),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Cards}`,
          },
        ]
      }
    />
  );
}
